import redditDeviceOptionsQuery from 'wb-reddit/app/routes/superGrid/targeting/api/devices.graphql';
import redditCarrierOptionsQuery from 'wb-reddit/app/routes/superGrid/targeting/api/carriers.graphql';
import redditLocationOptionsQuery from 'wb-reddit/app/routes/superGrid/targeting/api/locations.graphql';
import redditInterestsOptionsQuery from 'wb-reddit/app/routes/superGrid/targeting/api/interests.graphql';
import redditCustomAudienceOptionsQuery from 'wb-reddit/app/routes/superGrid/targeting/api/customAudiences.graphql';
import {
	CarrierOptionsQuery,
	CarrierOptionsQueryVariables,
	DeviceOptionsQuery,
	DeviceOptionsQueryVariables,
	InterestOptionsQuery,
	InterestOptionsQueryVariables,
	LocationOptionsQuery,
	LocationOptionsQueryVariables,
	RedditCustomAudienceOptionsQuery,
	RedditCustomAudienceOptionsQueryVariables,
	RedditTargetingLabelsByIdDocument,
} from 'wb-reddit/graphql/generated';
import { ApolloClient } from '@apollo/client';
import debounce from 'debounce-promise';
import { getApolloClient } from 'auth/lib/util';

type GetRedditDeviceOptionsParams = {
	client: ApolloClient<any>
	deviceType?: { value: string }[]
	search?: string
};

type GetRedditCarrierOptionsParams = {
	client: ApolloClient<any>
	search?: string
};

type GetRedditLocationOptionsParams = {
	client: ApolloClient<any>
	search?: string
};

type GetRedditInterestOptionsParams = {
	client: ApolloClient<any>
	search?: string
};

type GetRedditCustomAudienceOptionsParams = {
	client: ApolloClient<any>
	search?: string
	adAccountId: string
};

export type RedditCategorisedInterestOptions = Record<string, InterestOptionsQuery['options']['items']>;

export const getRedditDeviceOptions = debounce(async ({
	client,
	deviceType,
	search,
}: GetRedditDeviceOptionsParams) => {
	const { data } = await client.query<DeviceOptionsQuery, DeviceOptionsQueryVariables>({
		query:     redditDeviceOptionsQuery,
		variables: {
			search: `*${search ?? ''}*`,
		},
	});

	const filteredOptions = (data?.options?.items ?? []).filter(
		device => device?.label?.toLowerCase().includes(search?.toLowerCase() ?? ''),
	);

	deviceType = deviceType?.filter?.(type => ['DESKTOP', 'ANDROID', 'IOS'].includes(type.value));

	if ((deviceType?.length ?? 0) === 0) {
		return { options: filteredOptions, hasMore: !!data?.options?.cursor };
	}

	if (deviceType?.every?.(type => type.value === 'DESKTOP')) {
		return { options: [], hasMore: false };
	}

	let options: DeviceOptionsQuery['options']['items'] = [];

	if (deviceType?.some?.(type => type.value === 'ANDROID')) {
		options = [...(filteredOptions?.filter?.(device => device?.make !== 'Apple') ?? [])];
	}

	if (deviceType?.some?.(type => type.value === 'IOS')) {
		options = [...options, ...(filteredOptions?.filter?.(device => device?.make === 'Apple') ?? [])];
	}

	return { options, hasMore: !!data?.options?.cursor };
}, 300);

export const getRedditCarrierOptions = debounce(async ({
	client,
	search,
}: GetRedditCarrierOptionsParams) => {
	const { data } = await client.query<CarrierOptionsQuery, CarrierOptionsQueryVariables>({
		query:     redditCarrierOptionsQuery,
		variables: {
			search: `*${search ?? ''}*`,
		},
	});

	const filteredOptions = (data?.options?.items ?? []).filter(
		device => device?.label?.toLowerCase().includes(search?.toLowerCase() ?? ''),
	);

	return filteredOptions ?? [];
}, 300);

export const getRedditLocationOptions = debounce(async ({
	client,
	search,
}: GetRedditLocationOptionsParams) => {
	const { data } = await client.query<LocationOptionsQuery, LocationOptionsQueryVariables>({
		query:     redditLocationOptionsQuery,
		variables: {
			search: `*${search ?? ''}*`,
		},
	});

	const filteredOptions = (data?.options?.items ?? []).filter(
		device => device?.label?.toLowerCase().includes(search?.toLowerCase() ?? ''),
	)?.slice(0, 100);

	return { options: filteredOptions ?? [], hasMore: !!data?.options?.cursor };
}, 300);

export const getRedditInterestOptions = debounce(async ({
	client,
	search,
}: GetRedditInterestOptionsParams): Promise<RedditCategorisedInterestOptions> => {
	const { data } = await client.query<InterestOptionsQuery, InterestOptionsQueryVariables>({
		query:     redditInterestsOptionsQuery,
		variables: {
			search: `*${search ?? ''}*`,
		},
	});

	const filteredOptions = (data?.options?.items ?? []).filter(
		device => device?.label?.toLowerCase().includes(search?.toLowerCase() ?? ''),
	);

	return (filteredOptions ?? []).reduce((accu, option) => {
		accu[option?.category ?? 'unknown'] = [
			...(accu[option?.category ?? 'unknown'] ?? []),
			option,
		];

		return accu;
	}, {} as RedditCategorisedInterestOptions);
}, 300);

export const getRedditCustomAudienceOptions = async ({
	client,
	search,
	adAccountId,
}: GetRedditCustomAudienceOptionsParams) => {
	const { data } = await client.query<RedditCustomAudienceOptionsQuery, RedditCustomAudienceOptionsQueryVariables>({
		query:     redditCustomAudienceOptionsQuery,
		variables: {
			search: `*${search ?? ''}*`,
			adAccountId,
		},
	});

	const filteredOptions = (data?.options?.items ?? []).filter(
		audience => audience?.label?.toLowerCase().includes(search?.toLowerCase() ?? ''),
	);

	return filteredOptions ?? [];
};

export const getRedditTargetingsByIds = async ({
	locationIds,
	communityIds,
	interestIds,
	carrierIds,
	audienceIds,
}) => {
	const client = getApolloClient();

	if (!client) {
		return {
			locations:   [],
			communities: [],
			interests:   [],
			carriers:    [],
			audiences:   [],
		};
	}

	const { data } = await client.query({
		query:     RedditTargetingLabelsByIdDocument,
		variables: {
			locationIds,
			communityIds,
			interestIds,
			carrierIds,
			audienceIds,
		},
	});

	return {
		locations:   data?.targetingGeolocations?.items ?? [],
		communities: data?.targetingCommunities?.items ?? [],
		interests:   data?.targetingInterests?.items ?? [],
		carriers:    data?.targetingCarriers?.items ?? [],
		audiences:   data?.audiences?.items ?? [],
	};
};
